import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { z } from 'zod';
import { FormattedMessage } from 'react-intl';
import { useSelfServiceVerifyMagicLinkHook } from '@/api/trafikportalen';
import { useSelfServiceAuth } from '@/stores/selfServiceAuth';
import { useFavicon } from '@/hooks/useFavicon';
import { useTenantID } from '@/hooks/useTenantID';
import { Section } from '@/components/Section';
import { Loading } from '@/components/Loading';
import { IconExclamationTriangle, IconChevronLeft } from '@/icons';
import { cn } from '@/helpers/classnames';
import Button from '@/components/core/Button';

const verifySearchSchema = z.object({
  token: z.string(),
});

export const Route = createFileRoute('/$tenantId/self-service/auth/verify')({
  component: VerifyMagicLink,
  validateSearch: zodSearchValidator(verifySearchSchema),
});

function VerifyMagicLink() {
  const { token } = Route.useSearch();
  const tenantId = useTenantID();
  useFavicon(tenantId);
  const navigate = useNavigate();
  const { setToken } = useSelfServiceAuth();
  const verifyToken = useSelfServiceVerifyMagicLinkHook();
  const hasAttempted = useRef(false);

  const [error, setError] = useState(false);

  useEffect(() => {
    if (hasAttempted.current) return;
    if (!token) return;

    hasAttempted.current = true;

    verifyToken({ token, tenant_id: tenantId })
      .then((response) => {
        setToken(response.token, response.expires_at);
        navigate({
          to: '/$tenantId/self-service/dashboard',
          params: { tenantId },
          replace: true,
        });
      })
      .catch((err) => {
        console.error(err);
        setError(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tenantId]);

  if (error) {
    return (
      <div
        className={cn('flex grow items-center justify-center p-5', tenantId)}
      >
        <Section className="m-auto w-full max-w-md flex-col gap-6 !p-8">
          <div
            className="flex flex-col items-center gap-4 text-center"
            role="alert"
          >
            <div className="rounded-full bg-red-100 p-4">
              <IconExclamationTriangle
                className="size-8 text-red-700"
                aria-hidden="true"
              />
            </div>
            <h1 className="text-xl font-medium text-text-950">
              <FormattedMessage defaultMessage="Länken är ogiltig eller har gått ut" />
            </h1>
            <p className="text-text-700">
              <FormattedMessage defaultMessage="Be om en ny inloggningslänk och försök igen." />
            </p>
          </div>
          <Button
            variant="ghost"
            startIcon={<IconChevronLeft aria-hidden="true" />}
            to="/$tenantId/self-service/login"
            params={{ tenantId }}
            className="mx-auto"
          >
            <FormattedMessage defaultMessage="Begär ny länk" />
          </Button>
        </Section>
      </div>
    );
  }

  return (
    <div className={cn('flex grow items-center justify-center p-5', tenantId)}>
      <Section
        className="m-auto w-full max-w-md flex-col items-center gap-6 !p-8"
        aria-busy="true"
        role="status"
      >
        <Loading />
        <p className="text-text-700">
          <FormattedMessage defaultMessage="Verifierar..." />
        </p>
      </Section>
    </div>
  );
}
